import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { api } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import { useFeatures } from '../lib/features';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  TrendingUp, Plus, Download, MessageSquare, Phone,
  CheckCircle2, Clock, AlertCircle, X, Users,
  CalendarClock, Briefcase, Filter,
} from 'lucide-react';
import {
  ACTIVITY_TYPES, OUTCOMES, OPP_STAGES, activityTypesFor, activityLabel,
  labelFor, colorFor, todayISO, showDay, apiError, EmptyMsg, StatusPill, ClientPicker, AttachmentLinks,
} from '../components/sales/salesShared';
import ActivityModal from '../components/sales/ActivityModal';
import ClientTimelineModal from '../components/sales/ClientTimelineModal';
import CallListTab from '../components/sales/CallListTab';
import NeedsDetailsTab from '../components/sales/NeedsDetailsTab';
import TeamTab from '../components/sales/TeamTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;


// ============================================================================
// Opportunity Modal
// ============================================================================
const OpportunityModal = ({ open, onClose, onSaved, editing = null }) => {
  const [form, setForm] = useState({
    advertiser_id: '', advertiser_name: '', title: '', product_or_service: '', estimated_value: '',
    stage: 'lead', expected_close_date: '', notes: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(editing ? {
        advertiser_id: editing.advertiser_id,
        advertiser_name: editing.advertiser_name || '',
        title: editing.title || '',
        product_or_service: editing.product_or_service || '',
        estimated_value: editing.estimated_value ?? '',
        stage: editing.stage || 'lead',
        expected_close_date: editing.expected_close_date || '',
        notes: editing.notes || '',
      } : {
        advertiser_id: '', advertiser_name: '', title: '', product_or_service: '', estimated_value: '',
        stage: 'lead', expected_close_date: '', notes: '',
      });
    }
  }, [open, editing]);

  const submit = async () => {
    if (!form.advertiser_id || !form.title.trim()) return;
    setSubmitting(true);
    try {
      const { advertiser_name: _name, ...rest } = form;
      const payload = {
        ...rest,
        estimated_value: parseFloat(form.estimated_value) || 0,
        expected_close_date: form.expected_close_date || null,
      };
      if (editing) {
        await api.put(`/sales/opportunities/${editing.id}`, payload);
      } else {
        await api.post('/sales/opportunities', payload);
      }
      onSaved();
      onClose();
    } catch (e) {
      alert('Save failed: ' + apiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#121212] border-[#1E293B] text-[#F8FAFC] max-w-xl" data-testid="opportunity-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Briefcase className="w-5 h-5 text-[#3B82F6]" /> {editing ? 'Edit Opportunity' : 'New Opportunity'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-[#94A3B8]">Client *</Label>
            <ClientPicker
              value={form.advertiser_id}
              valueLabel={form.advertiser_name}
              onChange={(c) => setForm(f => ({ ...f, advertiser_id: c.id, advertiser_name: c.business_name }))}
              disabled={!!editing}
              testid="opp-client-select"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-[#94A3B8]">Title *</Label>
            <Input data-testid="opp-title-input" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder='e.g. "New storefront sign + window decals"' className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Product / Service</Label>
              <Input value={form.product_or_service} onChange={e => setForm(f => ({ ...f, product_or_service: e.target.value }))} className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]" />
            </div>
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Estimated Value ($)</Label>
              <Input type="number" step="0.01" min="0" value={form.estimated_value} onChange={e => setForm(f => ({ ...f, estimated_value: e.target.value }))} className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Stage</Label>
              <Select value={form.stage} onValueChange={v => setForm(f => ({ ...f, stage: v }))}>
                <SelectTrigger className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#121212] border-[#1E293B]">
                  {OPP_STAGES.map(s => <SelectItem key={s.value} value={s.value} className="text-[#F8FAFC]">{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Expected Close Date</Label>
              <Input type="date" value={form.expected_close_date} onChange={e => setForm(f => ({ ...f, expected_close_date: e.target.value }))} className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]" />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-[#94A3B8]">Notes</Label>
            <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] min-h-[80px]" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="border-[#1E293B] bg-[#121212] text-[#F8FAFC]">Cancel</Button>
            <Button onClick={submit} disabled={submitting || !form.advertiser_id || !form.title.trim()} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="opp-submit-btn">
              {submitting ? 'Saving…' : (editing ? 'Save Changes' : 'Create Opportunity')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};


// ============================================================================
// Main Sales Page
// ============================================================================
const SalesPage = () => {
  const { user } = useAuth();
  const { salesOnly } = useFeatures();
  const isSalesRep = user?.role === 'sales_rep';
  const hasMatchedRepProfile = !!user?.matched_sales_rep_id;
  const [tab, setTab] = useState('myday');
  const [activities, setActivities] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [salesReps, setSalesReps] = useState([]);
  const [facets, setFacets] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activityModalOpen, setActivityModalOpen] = useState(false);
  const [oppModalOpen, setOppModalOpen] = useState(false);
  const [editingOpp, setEditingOpp] = useState(null);
  const [prefillClient, setPrefillClient] = useState(null);
  const [viewClientId, setViewClientId] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);

  // Managers can look at one rep at a time; reps always see their own clients.
  const [repFilter, setRepFilter] = useState('all');
  const clientRepId = !isSalesRep && repFilter !== 'all' ? repFilter : null;

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterOutcome, setFilterOutcome] = useState('all');

  const loadAll = useCallback(async () => {
    const rep = clientRepId ? `&client_rep_id=${encodeURIComponent(clientRepId)}` : '';
    try {
      const [acts, opps, dash, reps, fac] = await Promise.all([
        api.get(`/sales/activities?limit=200${rep}`),
        api.get(`/sales/opportunities?_=1${rep}`),
        api.get(`/sales/dashboard?today=${todayISO()}${rep}`),
        api.get('/sales-reps'),
        api.get('/advertisers/facets'),
      ]);
      setActivities(acts);
      setOpportunities(opps);
      setDashboard(dash);
      setSalesReps(reps);
      setFacets(fac);
    } catch (e) {
      console.error('Sales load failed', e);
    } finally {
      setLoading(false);
    }
  }, [clientRepId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleExportCsv = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/sales/activities/export-csv`, {
        withCredentials: true, responseType: 'blob',
        params: clientRepId ? { client_rep_id: clientRepId } : {},
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'sales_activities.csv'; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { alert('Export failed'); }
  };

  const handleMarkFollowupComplete = async (id) => {
    try {
      await api.put(`/sales/activities/${id}`, { follow_up_completed: true });
      await loadAll();
    } catch (e) { alert('Failed: ' + apiError(e)); }
  };

  const logFor = (client) => { setPrefillClient(client || null); setActivityModalOpen(true); };

  // Pipeline drag-and-drop: drop a card on a column to change its stage.
  const moveOpportunity = async (oppId, stage) => {
    const opp = opportunities.find(o => o.id === oppId);
    if (!opp || opp.stage === stage) return;
    setOpportunities(list => list.map(o => (o.id === oppId ? { ...o, stage } : o)));   // move at once
    try {
      await api.put(`/sales/opportunities/${oppId}`, { stage });
    } catch (e) {
      alert('Could not move the opportunity: ' + apiError(e));
    }
    loadAll();
  };

  const filteredActivities = useMemo(() => {
    return activities.filter(a => {
      if (filterType !== 'all' && a.activity_type !== filterType) return false;
      if (filterOutcome !== 'all' && a.outcome !== filterOutcome) return false;
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        if (!a.summary?.toLowerCase().includes(q)
          && !a.advertiser_name?.toLowerCase().includes(q)
          && !a.notes?.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [activities, filterType, filterOutcome, searchTerm]);

  const oppsByStage = useMemo(() => {
    const m = {};
    OPP_STAGES.forEach(s => { m[s.value] = []; });
    opportunities.forEach(o => { (m[o.stage] = m[o.stage] || []).push(o); });
    return m;
  }, [opportunities]);

  if (loading) {
    return <div className="text-center py-12 text-[#94A3B8]">Loading sales data…</div>;
  }

  const repName = clientRepId ? (salesReps.find(r => r.id === clientRepId)?.name || 'this rep') : null;
  const whose = isSalesRep ? 'your' : (repName ? `${repName}'s` : "everyone's");
  const Whose = whose.charAt(0).toUpperCase() + whose.slice(1);
  // The call list and the research queue start on the same clients the rest
  // of the page is showing.
  const listRep = isSalesRep ? 'mine' : (clientRepId || 'all');

  // Reps start on their own clients, so the tab counts their own too.
  const needsDetailsCount = (isSalesRep ? facets?.needs_details_mine : facets?.needs_details) || 0;

  const tabs = [
    { k: 'myday', l: isSalesRep ? 'My day' : (repName ? `${repName}'s day` : 'Today') },
    { k: 'calls', l: 'Call list' },
    { k: 'activities', l: 'Activity Log' },
    { k: 'pipeline', l: 'Pipeline' },
    { k: 'details', l: `Needs details${needsDetailsCount ? ` (${needsDetailsCount.toLocaleString()})` : ''}` },
    ...(!isSalesRep ? [{ k: 'team', l: 'Team' }] : []),
    { k: 'reports', l: 'Reports' },
  ];

  return (
    <div className="space-y-5" data-testid="sales-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-[#F8FAFC] tracking-tight flex items-center gap-3">
            <TrendingUp className="w-7 h-7 text-[#3B82F6]" /> Sales
          </h1>
          <p className="text-sm text-[#94A3B8] mt-1">
            {isSalesRep
              ? 'Your clients, your follow-ups and who to call next.'
              : 'Track activities, follow-ups, and opportunities across your client base.'}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!isSalesRep && (
            <Select value={repFilter} onValueChange={setRepFilter}>
              <SelectTrigger className="bg-[#121212] border-[#1E293B] text-[#F8FAFC] w-[180px]" data-testid="sales-rep-filter">
                <Users className="w-4 h-4 mr-2 text-[#94A3B8]" /><SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-[#1E293B]">
                <SelectItem value="all" className="text-[#F8FAFC]">All reps</SelectItem>
                {salesReps.map(r => <SelectItem key={r.id} value={r.id} className="text-[#F8FAFC]">{r.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Button onClick={() => logFor(null)} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="new-activity-btn">
            <Plus className="w-4 h-4 mr-2" /> Log Activity
          </Button>
          <Button onClick={() => { setEditingOpp(null); setOppModalOpen(true); }} variant="outline" className="border-[#1E293B] text-[#94A3B8] hover:bg-[#1A1A1A] hover:text-[#F8FAFC]" data-testid="new-opportunity-btn">
            <Briefcase className="w-4 h-4 mr-2" /> New Opportunity
          </Button>
        </div>
      </div>

      {isSalesRep && !hasMatchedRepProfile && (
        <div
          className="p-3 bg-amber-900/20 border border-amber-700/50 rounded-md flex items-start gap-3"
          data-testid="sales-no-match-banner"
        >
          <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-amber-200">
            <p className="font-medium">No matching sales rep profile was found for your user account.</p>
            <p className="text-xs text-amber-200/80 mt-1">
              No clients can be assigned to you yet. Ask an admin to put your email address
              ({user?.email}) on your Sales Rep record in Settings.
            </p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[#1E293B] overflow-x-auto">
        {tabs.map(t => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            data-testid={`sales-tab-${t.k}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              tab === t.k ? 'border-[#3B82F6] text-[#F8FAFC]' : 'border-transparent text-[#94A3B8] hover:text-[#F8FAFC]'
            }`}
          >{t.l}</button>
        ))}
      </div>

      {/* === MY DAY === */}
      {tab === 'myday' && dashboard && (
        <div className="space-y-5" data-testid="my-day">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <KPICard label={isSalesRep ? 'My clients' : 'Assigned clients'} value={dashboard.my_clients.toLocaleString()} accent="#F8FAFC" icon={Users} testid="kpi-my-clients" />
            <KPICard label="Follow-ups Due Today" value={dashboard.follow_due_today.length} accent="#3B82F6" icon={CalendarClock} testid="kpi-due-today" />
            <KPICard label="Overdue Follow-ups" value={dashboard.follow_overdue.length} accent="#EF4444" icon={AlertCircle} testid="kpi-overdue" />
            <KPICard label="Not contacted yet" value={dashboard.call_next_count.toLocaleString()} accent="#F59E0B" icon={Phone} testid="kpi-call-next" />
            <KPICard label="Pipeline Value" value={`$${dashboard.pipeline_value.toLocaleString()}`} accent="#10B981" icon={TrendingUp} testid="kpi-pipeline" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card title={`Follow-ups due today (${dashboard.follow_due_today.length})`} testid="card-followups-today">
              {dashboard.follow_due_today.length === 0
                ? <EmptyMsg text="Nothing due today." />
                : dashboard.follow_due_today.map(a => (
                  <FollowupRow key={a.id} a={a} onOpen={() => setViewClientId(a.advertiser_id)} onComplete={() => handleMarkFollowupComplete(a.id)} />
                ))}
            </Card>
            <Card title={`Overdue follow-ups (${dashboard.follow_overdue.length})`} testid="card-followups-overdue">
              {dashboard.follow_overdue.length === 0
                ? <EmptyMsg text="No overdue follow-ups." />
                : dashboard.follow_overdue.map(a => (
                  <FollowupRow key={a.id} a={a} overdue onOpen={() => setViewClientId(a.advertiser_id)} onComplete={() => handleMarkFollowupComplete(a.id)} />
                ))}
            </Card>

            <Card
              title={`Call next (${dashboard.call_next_count.toLocaleString()})`}
              subtitle={`${Whose} clients with a phone number and no contact yet`}
              testid="card-call-next"
              action={dashboard.call_next_count > dashboard.call_next.length
                ? <button type="button" onClick={() => setTab('calls')} className="text-xs text-[#3B82F6]" data-testid="open-call-list">Open the call list</button>
                : null}
            >
              {dashboard.call_next.length === 0
                ? <EmptyMsg text={dashboard.my_clients ? 'Everyone with a phone number has been contacted.' : 'No clients assigned yet.'} />
                : dashboard.call_next.map(c => (
                  <ClientRow key={c.id} c={c} onOpen={() => setViewClientId(c.id)} onLog={() => logFor(c)} testid={`call-next-${c.id}`}>
                    <a href={`tel:${c.phone}`} className="text-[#3B82F6]">{c.phone}</a>{c.industry ? ` · ${c.industry}` : ''}
                  </ClientRow>
                ))}
            </Card>

            {dashboard.rep_inactivity_days > 0 && dashboard.release_soon_count > 0 && (
              <Card
                title={`About to be released (${dashboard.release_soon_count.toLocaleString()})`}
                subtitle={`No activity in almost ${dashboard.rep_inactivity_days} days. Log an activity to keep the client; otherwise it goes back to the unassigned pool.`}
                testid="card-release-soon"
              >
                {dashboard.release_soon.map(c => (
                  <ClientRow key={c.id} c={c} onOpen={() => setViewClientId(c.id)} onLog={() => logFor(c)} testid={`release-client-${c.id}`}>
                    <span className="text-amber-400">
                      {c.days_until_release > 0 ? `${c.days_until_release} day${c.days_until_release === 1 ? '' : 's'} left` : 'Released at the next daily check'}
                    </span>
                    {' · '}Last contact: {c.last_contact_date ? showDay(c.last_contact_date) : 'never'}
                  </ClientRow>
                ))}
              </Card>
            )}

            <Card title={`Clients needing attention (${dashboard.clients_needing_attention_count.toLocaleString()})`} testid="card-attention" subtitle="Assigned, still open, and no contact in 30+ days">
              {dashboard.clients_needing_attention.length === 0
                ? <EmptyMsg text="All assigned clients were contacted recently." />
                : dashboard.clients_needing_attention.slice(0, 10).map(c => (
                  <ClientRow key={c.id} c={c} onOpen={() => setViewClientId(c.id)} onLog={() => logFor(c)} testid={`attention-client-${c.id}`}>
                    Last contact: {c.last_contact_date ? showDay(c.last_contact_date) : 'never'}
                  </ClientRow>
                ))}
            </Card>

            <Card title={`Recent activity (${dashboard.recent_activities.length})`} testid="card-recent-activities">
              {dashboard.recent_activities.length === 0
                ? <EmptyMsg text="Nothing logged yet — click 'Log Activity' above to start." />
                : dashboard.recent_activities.slice(0, 8).map(a => (
                  <ActivityRow key={a.id} a={a} compact salesOnly={salesOnly} onOpen={() => setViewClientId(a.advertiser_id)} />
                ))}
            </Card>

            <Card title="Clients by status" subtitle={`Where ${whose} assigned clients stand`} testid="card-by-status">
              <div className="flex flex-wrap gap-2 py-2">
                {OUTCOMES.filter(o => dashboard.clients_by_status[o.value]).map(o => (
                  <span key={o.value} className="inline-flex items-center gap-2">
                    <StatusPill status={o.value} />
                    <span className="font-mono text-sm text-[#F8FAFC]">{dashboard.clients_by_status[o.value].toLocaleString()}</span>
                  </span>
                ))}
                {Object.keys(dashboard.clients_by_status).length === 0 && <EmptyMsg text="No assigned clients yet." />}
              </div>
            </Card>
          </div>

          {dashboard.activity_count_by_rep.length > 0 && !clientRepId && (
            <Card title="Activity by Sales Rep" subtitle="All time. See the Team tab for a period." testid="card-by-rep">
              <div className="space-y-2">
                {dashboard.activity_count_by_rep.map(r => (
                  <div key={r.sales_rep_id} className="flex items-center justify-between py-1.5">
                    <span className="text-sm text-[#F8FAFC]">{r.sales_rep_name}</span>
                    <span className="font-mono text-sm text-[#94A3B8]">{r.count} activities</span>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}

      {/* === CALL LIST === */}
      {tab === 'calls' && (
        <CallListTab salesReps={salesReps} facets={facets} defaultRep={listRep} onChanged={loadAll} />
      )}

      {/* === ACTIVITY LOG === */}
      {tab === 'activities' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3 bg-[#121212] border border-[#1E293B] rounded-lg p-3">
            <Filter className="w-4 h-4 text-[#94A3B8]" />
            <Input
              placeholder="Search summary, client, or notes…"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] flex-1 min-w-[200px]"
              data-testid="activity-search-input"
            />
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] w-[180px]"><SelectValue placeholder="All activities" /></SelectTrigger>
              <SelectContent className="bg-[#121212] border-[#1E293B]">
                <SelectItem value="all" className="text-[#F8FAFC]">All activities</SelectItem>
                {activityTypesFor(salesOnly).map(a => <SelectItem key={a.value} value={a.value} className="text-[#F8FAFC]">{a.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterOutcome} onValueChange={setFilterOutcome}>
              <SelectTrigger className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] w-[170px]"><SelectValue placeholder="All outcomes" /></SelectTrigger>
              <SelectContent className="bg-[#121212] border-[#1E293B]">
                <SelectItem value="all" className="text-[#F8FAFC]">All outcomes</SelectItem>
                {OUTCOMES.map(o => <SelectItem key={o.value} value={o.value} className="text-[#F8FAFC]">{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={handleExportCsv} className="border-[#1E293B] text-[#94A3B8] hover:bg-[#1A1A1A]" data-testid="export-csv-btn">
              <Download className="w-4 h-4 mr-1" /> CSV
            </Button>
          </div>

          <p className="text-xs text-[#475569]">Showing the latest {activities.length} activities for {whose} clients.</p>
          <div className="bg-[#121212] border border-[#1E293B] rounded-lg overflow-hidden divide-y divide-[#1E293B]">
            {filteredActivities.length === 0
              ? <EmptyMsg text="No activities match your filters." />
              : filteredActivities.map(a => <ActivityRow key={a.id} a={a} salesOnly={salesOnly} onOpen={() => setViewClientId(a.advertiser_id)} onComplete={() => handleMarkFollowupComplete(a.id)} />)
            }
          </div>
        </div>
      )}

      {/* === PIPELINE === */}
      {tab === 'pipeline' && (
        <div className="space-y-4">
          <p className="text-sm text-[#94A3B8]">Drag an opportunity to another column to change its stage, or click it to edit.</p>
          <div className="grid grid-cols-1 md:grid-cols-7 gap-3 overflow-x-auto">
            {OPP_STAGES.map(stage => (
              <div
                key={stage.value}
                className={`bg-[#0A0A0A] border rounded-lg p-3 min-w-[180px] transition-colors ${dragOverStage === stage.value ? 'border-[#3B82F6]' : 'border-[#1E293B]'}`}
                data-testid={`pipeline-col-${stage.value}`}
                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (dragOverStage !== stage.value) setDragOverStage(stage.value); }}
                onDragLeave={() => setDragOverStage(s => (s === stage.value ? null : s))}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOverStage(null);
                  const id = e.dataTransfer.getData('text/plain');
                  if (id) moveOpportunity(id, stage.value);
                }}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: stage.color }} />
                    <span className="text-xs font-semibold text-[#F8FAFC] uppercase tracking-wide">{stage.label}</span>
                  </div>
                  <span className="text-xs text-[#475569] font-mono">{oppsByStage[stage.value]?.length || 0}</span>
                </div>
                <div className="space-y-2 min-h-[48px]">
                  {(oppsByStage[stage.value] || []).map(o => (
                    <div
                      key={o.id}
                      draggable
                      onDragStart={(e) => { e.dataTransfer.setData('text/plain', o.id); e.dataTransfer.effectAllowed = 'move'; }}
                      onClick={() => { setEditingOpp(o); setOppModalOpen(true); }}
                      className="cursor-grab active:cursor-grabbing bg-[#121212] border border-[#1E293B] rounded p-2.5 hover:border-[#3B82F6] transition-colors"
                      data-testid={`opp-card-${o.id}`}
                    >
                      <div className="text-xs text-[#94A3B8] mb-1">{o.advertiser_name}</div>
                      <div className="text-sm font-medium text-[#F8FAFC] mb-1.5">{o.title}</div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-mono text-[#10B981]">${(o.estimated_value || 0).toLocaleString()}</span>
                        {o.expected_close_date && <span className="text-[#475569]">{showDay(o.expected_close_date)}</span>}
                      </div>
                    </div>
                  ))}
                  {(oppsByStage[stage.value] || []).length === 0 && (
                    <div className="text-xs text-[#475569] py-3 text-center">Empty</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* === NEEDS DETAILS === */}
      {tab === 'details' && (
        <NeedsDetailsTab salesReps={salesReps} facets={facets} defaultRep={listRep} onChanged={loadAll} />
      )}

      {/* === TEAM === */}
      {tab === 'team' && !isSalesRep && (
        <TeamTab onPickRep={(id) => { setRepFilter(id); setTab('myday'); }} />
      )}

      {/* === REPORTS === */}
      {tab === 'reports' && dashboard && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <KPICard label="Won Opportunities" value={dashboard.opportunities_won} accent="#10B981" icon={CheckCircle2} />
          <KPICard label="Lost Opportunities" value={dashboard.opportunities_lost} accent="#EF4444" icon={X} />
          <KPICard label="Assigned clients stale 90+ days" value={dashboard.clients_stale_90_days.toLocaleString()} accent="#F59E0B" icon={Clock} />
          <KPICard label="Open Pipeline Value" value={`$${dashboard.pipeline_value.toLocaleString()}`} accent="#3B82F6" icon={TrendingUp} />
          <KPICard label="Recent Activities (loaded)" value={activities.length} accent="#A855F7" icon={MessageSquare} />
          <KPICard label="Total Opportunities" value={opportunities.length} accent="#06B6D4" icon={Briefcase} />
        </div>
      )}

      <ActivityModal
        open={activityModalOpen}
        client={prefillClient}
        onClose={() => { setActivityModalOpen(false); setPrefillClient(null); }}
        onSaved={loadAll}
      />
      <OpportunityModal
        open={oppModalOpen}
        onClose={() => { setOppModalOpen(false); setEditingOpp(null); }}
        editing={editingOpp}
        onSaved={loadAll}
      />
      <ClientTimelineModal
        clientId={viewClientId}
        open={!!viewClientId}
        onClose={() => setViewClientId(null)}
        onLog={(c) => { setViewClientId(null); logFor(c); }}
        onChanged={loadAll}
      />
    </div>
  );
};


// --- Reusable components ---

const KPICard = ({ label, value, accent, icon: Icon, testid }) => (
  <div className="bg-[#121212] border border-[#1E293B] rounded-lg p-4" data-testid={testid}>
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs text-[#94A3B8] uppercase tracking-wide">{label}</span>
      {Icon && <Icon className="w-4 h-4" style={{ color: accent }} />}
    </div>
    <div className="text-2xl font-bold" style={{ color: accent }}>{value}</div>
  </div>
);

const Card = ({ title, subtitle, children, testid, action }) => (
  <div className="bg-[#121212] border border-[#1E293B] rounded-lg p-4" data-testid={testid}>
    <div className="mb-3 flex items-start justify-between gap-3">
      <div>
        <h3 className="text-sm font-semibold text-[#F8FAFC]">{title}</h3>
        {subtitle && <p className="text-xs text-[#475569] mt-0.5">{subtitle}</p>}
      </div>
      {action}
    </div>
    <div className="divide-y divide-[#1E293B]">{children}</div>
  </div>
);

const ClientRow = ({ c, children, onOpen, onLog, testid }) => (
  <div className="flex items-center justify-between py-2 gap-3" data-testid={testid}>
    <div className="min-w-0">
      <button type="button" onClick={onOpen} className="text-sm text-[#F8FAFC] hover:text-[#3B82F6] text-left truncate max-w-full">{c.business_name}</button>
      <div className="text-xs text-[#94A3B8] truncate">{children}</div>
    </div>
    <Button size="sm" variant="ghost" onClick={onLog} className="text-[#3B82F6] shrink-0" data-testid={`quick-log-${c.id}`}>
      <Plus className="w-3 h-3 mr-1" /> Log
    </Button>
  </div>
);

const FollowupRow = ({ a, overdue, onComplete, onOpen }) => (
  <div className="flex items-center justify-between py-2 gap-3" data-testid={`followup-row-${a.id}`}>
    <div className="flex-1 min-w-0">
      <button type="button" onClick={onOpen} className="text-sm text-[#F8FAFC] hover:text-[#3B82F6] text-left truncate max-w-full">{a.advertiser_name}</button>
      <div className="text-xs text-[#94A3B8] truncate">{a.summary}</div>
      <div className={`text-xs mt-0.5 ${overdue ? 'text-red-400' : 'text-[#475569]'}`}>
        Due {showDay(a.follow_up_date)}
      </div>
    </div>
    {onComplete && (
      <Button size="sm" variant="ghost" onClick={onComplete} className="text-emerald-400 hover:text-emerald-300" title="Mark follow-up done" data-testid={`mark-done-${a.id}`}>
        <CheckCircle2 className="w-4 h-4" />
      </Button>
    )}
  </div>
);

const ActivityRow = ({ a, compact, onComplete, onOpen, salesOnly }) => {
  const T = ACTIVITY_TYPES.find(t => t.value === a.activity_type);
  const Icon = T?.icon || MessageSquare;
  const outcomeColor = colorFor(OUTCOMES, a.outcome);
  return (
    <div className={`flex items-start gap-3 ${compact ? 'py-2' : 'py-3 px-4'}`} data-testid={`activity-row-${a.id}`}>
      <div className="mt-0.5 shrink-0">
        <Icon className="w-4 h-4 text-[#3B82F6]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <button type="button" onClick={onOpen} className="text-sm font-medium text-[#F8FAFC] hover:text-[#3B82F6] text-left">{a.advertiser_name}</button>
          {a.outcome && (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wide" style={{ backgroundColor: `${outcomeColor}22`, color: outcomeColor }}>
              {labelFor(OUTCOMES, a.outcome)}
            </span>
          )}
        </div>
        <div className="text-sm text-[#94A3B8] mt-0.5">{a.summary}</div>
        {!compact && a.notes && <div className="text-xs text-[#475569] mt-1 whitespace-pre-wrap">{a.notes}</div>}
        <AttachmentLinks activity={a} className="mt-1" />
        <div className="flex items-center gap-3 text-xs text-[#475569] mt-1.5 flex-wrap">
          <span>{activityLabel(a.activity_type, salesOnly)}</span>
          {a.sales_rep_name && <span>· {a.sales_rep_name}</span>}
          <span>· {new Date(a.occurred_at || a.created_at).toLocaleString()}</span>
          {a.follow_up_date && !a.follow_up_completed && (
            <span className={`${a.follow_up_date < todayISO() ? 'text-red-400' : 'text-amber-400'}`}>
              · follow up {showDay(a.follow_up_date)}
            </span>
          )}
          {a.follow_up_completed && <span className="text-emerald-400">· follow up done</span>}
        </div>
      </div>
      {!compact && a.follow_up_date && !a.follow_up_completed && onComplete && (
        <Button size="sm" variant="ghost" onClick={onComplete} className="text-emerald-400 hover:text-emerald-300" title="Mark follow-up complete" data-testid={`activity-mark-done-${a.id}`}>
          <CheckCircle2 className="w-4 h-4" />
        </Button>
      )}
    </div>
  );
};

export default SalesPage;
